import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ApiService } from '../../../services/api.service';

@Component({
  selector: 'app-supplier-view-details',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './supplier-view-details.component.html',
  styleUrls: ['./supplier-view-details.component.css']
})
export class SupplierViewDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('archivedQuoteTableContainer') archivedQuoteTableContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('activeQuoteTableContainer') activeQuoteTableContainer?: ElementRef<HTMLDivElement>;
  supplier: any = null;
  activeTab = 'Profile';
  loading = false;
  
  // Product Category tab data
  categories: any[] = [];
  filteredCategories: any[] = [];
  searchTerm = '';
  selectedCategoryFilter = '';
  selectedStatusFilter = '';
  loadingCategories = false;
  categoryCount = 0;

  // Active Quote tab data
  activeQuotes: any[] = [];
  filteredActiveQuotes: any[] = [];
  quoteSearchTerm = '';
  selectedQuoteCategoryFilter = '';
  selectedValidityDateFilter = '';
  loadingQuotes = false;
  isLoadingMoreQuotes = false;
  quotePage = 1;
  quoteLimit = 5;
  quoteTotalItems = 0;
  quoteTotalPages = 0;
  hasMoreQuotes = true;
  quoteCategories: any[] = [];

  private activeQuoteScrollHandler: any = null;

  // Archived Quote tab data
  archivedQuotes: any[] = [];
  filteredArchivedQuotes: any[] = [];
  archivedQuoteSearchTerm = '';
  selectedArchivedQuoteCategoryFilter = '';
  selectedArchivedValidityDateFilter = '';
  loadingArchivedQuotes = false;
  isLoadingMoreArchivedQuotes = false;
  archivedQuotePage = 1;
  archivedQuoteLimit = 5;
  archivedQuoteTotalItems = 0;
  archivedQuoteTotalPages = 0;
  hasMoreArchivedQuotes = true;
  archivedQuoteCategories: any[] = [];

  private archivedQuoteScrollHandler: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService
  ) {}

  ngOnInit() {
    // Get supplier ID from route params
    const supplierId = this.route.snapshot.paramMap.get('id');
    if (supplierId) {
      this.loadSupplier(supplierId);
    }
  }

  ngAfterViewInit() {
    // Attach scroll listener after view is initialized
    // Only attach if we're on the relevant tab
    if (this.activeTab === 'Archived Quote') {
      setTimeout(() => {
        this.attachArchivedQuoteScrollListener();
      }, 100);
    } else if (this.activeTab === 'Active Quote') {
      setTimeout(() => {
        this.attachActiveQuoteScrollListener();
      }, 100);
    }
  }

  ngOnDestroy() {
    // Remove scroll event listeners when component is destroyed
    if (this.archivedQuoteScrollHandler && this.archivedQuoteTableContainer && this.archivedQuoteTableContainer.nativeElement) {
      this.archivedQuoteTableContainer.nativeElement.removeEventListener('scroll', this.archivedQuoteScrollHandler);
    }
    if (this.activeQuoteScrollHandler && this.activeQuoteTableContainer && this.activeQuoteTableContainer.nativeElement) {
      this.activeQuoteTableContainer.nativeElement.removeEventListener('scroll', this.activeQuoteScrollHandler);
    }
  }

  attachArchivedQuoteScrollListener() {
    if (this.archivedQuoteTableContainer && this.archivedQuoteTableContainer.nativeElement) {
      // Remove existing listener if any
      if (this.archivedQuoteScrollHandler) {
        this.archivedQuoteTableContainer.nativeElement.removeEventListener('scroll', this.archivedQuoteScrollHandler);
      }
      
      // Add scroll event listener to table container with throttling
      this.archivedQuoteScrollHandler = this.throttle(() => {
        this.onArchivedQuoteTableScroll();
      }, 200); // Throttle to 200ms to prevent too many calls
      
      this.archivedQuoteTableContainer.nativeElement.addEventListener('scroll', this.archivedQuoteScrollHandler, { passive: true });
    }
  }

  attachActiveQuoteScrollListener() {
    if (this.activeQuoteTableContainer && this.activeQuoteTableContainer.nativeElement) {
      // Remove existing listener if any
      if (this.activeQuoteScrollHandler) {
        this.activeQuoteTableContainer.nativeElement.removeEventListener('scroll', this.activeQuoteScrollHandler);
      }
      
      // Add scroll event listener to table container with throttling
      this.activeQuoteScrollHandler = this.throttle(() => {
        this.onActiveQuoteTableScroll();
      }, 200); // Throttle to 200ms to prevent too many calls
      
      this.activeQuoteTableContainer.nativeElement.addEventListener('scroll', this.activeQuoteScrollHandler, { passive: true });
    }
  }

  // Throttle function to limit scroll event calls
  private throttle(func: Function, limit: number) {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, ...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  loadSupplier(supplierId: string) {
    this.loading = true;
    // Fetch supplier details by ID
    this.apiService.getUserById(supplierId).subscribe({
      next: (supplier: any) => {
        // Parse phone number to extract country code and phone
        let phoneCountryCode = 'IN +91';
        let phoneNumber = supplier.phone || '';
        
        if (phoneNumber && phoneNumber.includes('+')) {
          // Try to match format like "IN +91 1234567890" or "+91 1234567890"
          const match = phoneNumber.match(/^([A-Z]{2}\s*\+\d+|\+\d+)\s+(.+)$/);
          if (match) {
            phoneCountryCode = match[1].trim();
            phoneNumber = match[2].trim();
          }
        }
        
        // Map supplier data
        // Use supplierId from database if available, otherwise fallback to generated ID
        let supplierId = supplier.supplierId;
        if (!supplierId) {
          supplierId = supplier._id ? `S${supplier._id.toString().slice(-6)}` : `S${String(1).padStart(6, '0')}`;
        }
        this.supplier = {
          ...supplier,
          supplierId: supplierId,
          role: supplier.userType === 'Supplier' ? 'Supplier' : supplier.userType || 'Supplier',
          phoneCountryCode: phoneCountryCode,
          phone: phoneNumber,
          companyName: supplier.companyName || supplier.name,
          firstName: supplier.firstName || (supplier.name ? supplier.name.split(' ')[0] : ''),
          lastName: supplier.lastName || (supplier.name ? supplier.name.split(' ').slice(1).join(' ') : ''),
          country: supplier.country || 'India',
          isActive: supplier.isActive !== undefined ? supplier.isActive : true
        };
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading supplier:', err);
        this.loading = false;
        // Navigate back if supplier not found
        this.router.navigate(['/supplier']);
      }
    });
  }

  formatDate(date: any): string {
    if (!date) return '--';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  formatCount(count: number): string {
    if (count === 0) return '00';
    return count.toString().padStart(2, '0');
  }

  onGoBack() {
    this.router.navigate(['/supplier']);
  }

  // Product Category tab methods
  onTabChange(tab: string) {
    this.activeTab = tab;
    if (tab === 'Product Category' && this.supplier && this.categories.length === 0) {
      this.loadCategories();
    } else if (tab === 'Active Quote') {
      if (this.supplier && this.activeQuotes.length === 0) {
        this.loadActiveQuotes(true);
      }
      // Wait for view to initialize, then attach scroll listener
      setTimeout(() => {
        this.attachActiveQuoteScrollListener();
      }, 200);
    } else if (tab === 'Archived Quote') {
      if (this.supplier && this.archivedQuotes.length === 0) {
        this.loadArchivedQuotes(true);
      }
      // Wait for view to initialize, then attach scroll listener
      setTimeout(() => {
        this.attachArchivedQuoteScrollListener();
      }, 200);
    }
  }

  loadCategories() {
    if (!this.supplier || !this.supplier._id) {
      return;
    }

    this.loadingCategories = true;
    this.apiService.getCategoriesBySupplier(this.supplier._id).subscribe({
      next: (response: any) => {
        this.categories = response.categories || [];
        this.categoryCount = response.total || 0;
        this.applyFilters();
        this.loadingCategories = false;
      },
      error: (err) => {
        console.error('Error loading categories:', err);
        this.categories = [];
        this.filteredCategories = [];
        this.categoryCount = 0;
        this.loadingCategories = false;
      }
    });
  }

  applyFilters() {
    let filtered = [...this.categories];

    // Search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(category =>
        category.name?.toLowerCase().includes(searchLower)
      );
    }

    // Category filter (if needed in future)
    if (this.selectedCategoryFilter) {
      // Add category-specific filtering logic here if needed
    }

    // Status filter
    if (this.selectedStatusFilter) {
      if (this.selectedStatusFilter === 'Active') {
        filtered = filtered.filter(category => category.isActive === true);
      } else if (this.selectedStatusFilter === 'Deactive') {
        filtered = filtered.filter(category => category.isActive === false);
      }
    }

    this.filteredCategories = filtered;
  }

  onSearchChange() {
    this.applyFilters();
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedCategoryFilter = '';
    this.selectedStatusFilter = '';
    this.applyFilters();
  }

  getStatusClass(isActive: boolean): string {
    return isActive ? 'status-active' : 'status-deactive';
  }

  getStatusText(isActive: boolean): string {
    return isActive ? 'Active' : 'Deactive';
  }

  getUserRole(user: any): string {
    if (!user) return '';
    if (user.userType === 'Admin') return 'Admin';
    if (user.userType === 'ProcurementManager') return 'Procurement Manager';
    if (user.userType === 'Supplier') return 'Supplier';
    return user.userType || '';
  }

  navigateToCategory(categoryId: string) {
    // Navigate to category details or product list filtered by category
    this.router.navigate(['/product/category'], { queryParams: { category: categoryId } });
  }

  // Active Quote tab methods
  loadActiveQuotes(reset: boolean = false) {
    if (!this.supplier || !this.supplier._id) {
      return;
    }

    if (reset) {
      this.quotePage = 1;
      this.activeQuotes = [];
      this.hasMoreQuotes = true;
    }

    if (!this.hasMoreQuotes && !reset) {
      return;
    }

    if (reset) {
      this.loadingQuotes = true;
    } else {
      this.isLoadingMoreQuotes = true;
    }
    
    // Use getQuotesBySupplier with pagination parameters
    this.apiService.getQuotesBySupplier(
      this.supplier._id,
      this.quotePage,
      this.quoteLimit,
      this.quoteSearchTerm || undefined,
      'active',
      this.selectedQuoteCategoryFilter || undefined
    ).subscribe({
      next: (response: any) => {
        // Handle both standardized and legacy response formats
        const data = response.data || response;
        const quotes = data.quotes || (Array.isArray(data) ? data : []);
        const pagination = data.pagination || {};
        
        if (reset) {
          this.activeQuotes = quotes;
        } else {
          this.activeQuotes = [...this.activeQuotes, ...quotes];
        }

        this.quoteTotalItems = pagination.totalItems || quotes.length;
        this.quoteTotalPages = pagination.totalPages || Math.ceil(quotes.length / this.quoteLimit);
        this.quoteCategories = data.categories || [];
        this.hasMoreQuotes = this.quotePage < this.quoteTotalPages;
        
        this.applyQuoteFilters();
        
        // Re-attach scroll listener after new data loads
        setTimeout(() => {
          this.attachActiveQuoteScrollListener();
        }, 100);
        
        this.loadingQuotes = false;
        this.isLoadingMoreQuotes = false;
      },
      error: (err) => {
        console.error('Error loading active quotes:', err);
        this.activeQuotes = [];
        this.filteredActiveQuotes = [];
        this.loadingQuotes = false;
        this.isLoadingMoreQuotes = false;
      }
    });
  }

  loadMoreQuotes() {
    // Don't load if already loading or no more data
    if (this.isLoadingMoreQuotes || !this.hasMoreQuotes || this.loadingQuotes) {
      return;
    }

    // Check if there are more pages
    if (this.quotePage < this.quoteTotalPages) {
      this.quotePage++;
      this.loadActiveQuotes(false);
    } else {
      this.hasMoreQuotes = false;
    }
  }

  onActiveQuoteTableScroll() {
    if (!this.activeQuoteTableContainer || !this.activeQuoteTableContainer.nativeElement) {
      return;
    }

    const container = this.activeQuoteTableContainer.nativeElement;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Load more when scrolled within 100px of bottom
    const threshold = 100;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    
    if (isNearBottom && this.hasMoreQuotes && !this.isLoadingMoreQuotes && !this.loadingQuotes) {
      this.loadMoreQuotes();
    }
  }

  applyQuoteFilters() {
    let filtered = [...this.activeQuotes];

    // Validity date filter (client-side only, as search and category are handled by backend)
    if (this.selectedValidityDateFilter) {
      const now = new Date();
      filtered = filtered.filter(quote => {
        if (!quote.validityDate) return false;
        const validityDate = new Date(quote.validityDate);
        
        if (this.selectedValidityDateFilter === 'expired') {
          return validityDate < now;
        } else if (this.selectedValidityDateFilter === 'active') {
          return validityDate >= now;
        }
        return true;
      });
    }

    this.filteredActiveQuotes = filtered;
  }

  onQuoteSearchChange() {
    this.quotePage = 1;
    this.loadActiveQuotes(true);
  }

  onQuoteCategoryFilterChange() {
    this.quotePage = 1;
    this.loadActiveQuotes(true);
  }

  onValidityDateFilterChange() {
    this.applyQuoteFilters();
  }

  resetQuoteFilters() {
    this.quoteSearchTerm = '';
    this.selectedQuoteCategoryFilter = '';
    this.selectedValidityDateFilter = '';
    this.quotePage = 1;
    this.loadActiveQuotes(true);
  }

  getQuotePriceRange(quote: any): { min: number; max: number; single: boolean } {
    if (!quote.quantityPrices || quote.quantityPrices.length === 0) {
      return { min: 0, max: 0, single: false };
    }

    const validPrices = quote.quantityPrices
      .filter((qp: any) => qp.unitPrice && qp.unitPrice > 0 && qp.quantity && qp.quantity > 0)
      .map((qp: any) => ({
        quantity: qp.quantity,
        unitPrice: qp.unitPrice,
        totalPrice: qp.totalPrice || (qp.quantity * qp.unitPrice)
      }))
      .sort((a: any, b: any) => a.quantity - b.quantity);

    if (validPrices.length === 0) {
      return { min: 0, max: 0, single: false };
    }

    const minPrice = Math.min(...validPrices.map((p: any) => p.totalPrice));
    const maxPrice = Math.max(...validPrices.map((p: any) => p.totalPrice));
    const single = minPrice === maxPrice;

    return { min: minPrice, max: maxPrice, single };
  }

  formatPrice(quote: any): string {
    const priceRange = this.getQuotePriceRange(quote);
    const currency = quote.currency || 'INR';
    
    if (priceRange.min === 0 && priceRange.max === 0) {
      return '--';
    }

    if (priceRange.single) {
      return `${this.formatNumber(priceRange.min)} ${currency}`;
    }

    return `Min: ${this.formatNumber(priceRange.min)} ${currency}, Max: ${this.formatNumber(priceRange.max)} ${currency}`;
  }

  formatNumber(num: number): string {
    if (!num) return '0';
    return num.toLocaleString('en-IN');
  }

  getShippingTermDisplay(quote: any): string {
    const shippingDetails = quote.product?.shippingDetails || quote.productDetails?.shippingDetails;
    if (!shippingDetails) return '--';

    const shippingTerm = shippingDetails.shippingTerm || '';
    let address = '';

    if (shippingTerm === 'DELIVERED') {
      const parts = [];
      if (shippingDetails.city) parts.push(shippingDetails.city);
      if (shippingDetails.state) parts.push(shippingDetails.state);
      if (shippingDetails.country) parts.push(shippingDetails.country);
      if (shippingDetails.pincode) parts.push(`PIN: ${shippingDetails.pincode}`);
      address = parts.join(', ');
    } else if (shippingTerm === 'FOB' || shippingTerm === 'CIF') {
      address = shippingDetails.mappedPort || '';
    }

    return `${shippingTerm}${address ? ', ' + address + '.' : ''}`;
  }

  navigateToProduct(productId: string) {
    this.router.navigate(['/product/view', productId]);
  }

  navigateToCategoryFromQuote(categoryId: string) {
    this.router.navigate(['/product/category'], { queryParams: { category: categoryId } });
  }


  // Archived Quote tab methods
  loadArchivedQuotes(reset: boolean = false) {
    if (!this.supplier || !this.supplier._id) {
      return;
    }

    if (reset) {
      this.archivedQuotePage = 1;
      this.archivedQuotes = [];
      this.hasMoreArchivedQuotes = true;
    }

    if (!this.hasMoreArchivedQuotes && !reset) {
      return;
    }

    if (reset) {
      this.loadingArchivedQuotes = true;
    } else {
      this.isLoadingMoreArchivedQuotes = true;
    }
    
    // Use getQuotesBySupplier with archived status
    this.apiService.getQuotesBySupplier(
      this.supplier._id,
      this.archivedQuotePage,
      this.archivedQuoteLimit,
      this.archivedQuoteSearchTerm || undefined,
      'archived',
      this.selectedArchivedQuoteCategoryFilter || undefined
    ).subscribe({
      next: (response: any) => {
        // Handle both standardized and legacy response formats
        const data = response.data || response;
        const quotes = data.quotes || (Array.isArray(data) ? data : []);
        const pagination = data.pagination || {};
        
        if (reset) {
          this.archivedQuotes = quotes;
        } else {
          this.archivedQuotes = [...this.archivedQuotes, ...quotes];
        }

        this.archivedQuoteTotalItems = pagination.totalItems || quotes.length;
        this.archivedQuoteTotalPages = pagination.totalPages || Math.ceil(quotes.length / this.archivedQuoteLimit);
        this.archivedQuoteCategories = data.categories || [];
        this.hasMoreArchivedQuotes = this.archivedQuotePage < this.archivedQuoteTotalPages;
        
        this.applyArchivedQuoteFilters();
        
        // Re-attach scroll listener after new data loads
        setTimeout(() => {
          this.attachArchivedQuoteScrollListener();
        }, 100);
        
        this.loadingArchivedQuotes = false;
        this.isLoadingMoreArchivedQuotes = false;
      },
      error: (err) => {
        console.error('Error loading archived quotes:', err);
        this.archivedQuotes = [];
        this.filteredArchivedQuotes = [];
        this.loadingArchivedQuotes = false;
        this.isLoadingMoreArchivedQuotes = false;
      }
    });
  }

  loadMoreArchivedQuotes() {
    // Don't load if already loading or no more data
    if (this.isLoadingMoreArchivedQuotes || !this.hasMoreArchivedQuotes || this.loadingArchivedQuotes) {
      return;
    }

    // Check if there are more pages
    if (this.archivedQuotePage < this.archivedQuoteTotalPages) {
      this.archivedQuotePage++;
      this.loadArchivedQuotes(false);
    } else {
      this.hasMoreArchivedQuotes = false;
    }
  }

  onArchivedQuoteTableScroll() {
    if (!this.archivedQuoteTableContainer || !this.archivedQuoteTableContainer.nativeElement) {
      return;
    }

    const container = this.archivedQuoteTableContainer.nativeElement;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Load more when scrolled within 100px of bottom
    const threshold = 100;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    
    if (isNearBottom && this.hasMoreArchivedQuotes && !this.isLoadingMoreArchivedQuotes && !this.loadingArchivedQuotes) {
      this.loadMoreArchivedQuotes();
    }
  }

  applyArchivedQuoteFilters() {
    let filtered = [...this.archivedQuotes];

    // Validity date filter (client-side only, as search and category are handled by backend)
    if (this.selectedArchivedValidityDateFilter) {
      const now = new Date();
      filtered = filtered.filter(quote => {
        if (!quote.validityDate) return false;
        const validityDate = new Date(quote.validityDate);
        
        if (this.selectedArchivedValidityDateFilter === 'expired') {
          return validityDate < now;
        } else if (this.selectedArchivedValidityDateFilter === 'active') {
          return validityDate >= now;
        }
        return true;
      });
    }

    this.filteredArchivedQuotes = filtered;
  }

  onArchivedQuoteSearchChange() {
    this.archivedQuotePage = 1;
    this.loadArchivedQuotes(true);
  }

  onArchivedQuoteCategoryFilterChange() {
    this.archivedQuotePage = 1;
    this.loadArchivedQuotes(true);
  }

  onArchivedValidityDateFilterChange() {
    this.applyArchivedQuoteFilters();
  }

  resetArchivedQuoteFilters() {
    this.archivedQuoteSearchTerm = '';
    this.selectedArchivedQuoteCategoryFilter = '';
    this.selectedArchivedValidityDateFilter = '';
    this.archivedQuotePage = 1;
    this.loadArchivedQuotes(true);
  }

}

